import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, AppBar, Toolbar, Typography, Button, Paper, ToggleButton, ToggleButtonGroup } from '@mui/material';
import './App.css';

// Components
import EstimatorWizard from './components/Estimator/EstimatorWizard';
import LoginPage from './components/Auth/LoginPage';
import AdminDashboard from './components/Admin/AdminDashboard';
import UserLeadsPage from './components/Admin/UserLeadsPage';

// Hooks
import { usePlatformSettings } from './hooks/usePlatformSettings';
import { translations } from './utils/translations';

// Warm, inviting color scheme
// Vibrant Coral #EB5E55 | Graphite #3A3335 | Raspberry Red #D81E5B | Papaya Whip #FDF0D5 | Ash Grey #C6D8D3
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#EB5E55', // Vibrant Coral
      dark: '#D94A41',
      light: '#F07A72',
    },
    secondary: {
      main: '#D81E5B', // Raspberry Red
      dark: '#B8184D',
      light: '#E54A7A',
    },
    background: {
      default: '#FDF0D5', // Papaya Whip
      paper: '#FFFFFF',
    },
    text: {
      primary: '#3A3335', // Graphite
      secondary: '#5C5658',
    },
    divider: '#C6D8D3', // Ash Grey
    error: {
      main: '#D81E5B',
    },
    warning: {
      main: '#EB5E55',
    },
    success: {
      main: '#4A9E7F',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
      color: '#3A3335',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          padding: '12px 28px',
          transition: 'all 0.2s ease',
          textTransform: 'none',
          fontSize: '1rem',
        },
        contained: {
          background: '#EB5E55',
          color: '#FFFFFF',
          fontWeight: 600,
          boxShadow: '0 4px 14px rgba(235, 94, 85, 0.3)',
          '&:hover': {
            background: '#D94A41',
            boxShadow: '0 6px 20px rgba(235, 94, 85, 0.4)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderColor: '#EB5E55',
          color: '#EB5E55',
          borderWidth: '2px',
          '&:hover': {
            borderColor: '#D94A41',
            backgroundColor: 'rgba(235, 94, 85, 0.06)',
            borderWidth: '2px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 1px 3px rgba(58,51,53,0.06), 0 4px 12px rgba(58,51,53,0.04)',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(58,51,53,0.08), 0 8px 24px rgba(58,51,53,0.06)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: '#FFFFFF',
          boxShadow: '0 1px 3px rgba(58,51,53,0.08)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: '#FFFFFF',
          border: '1px solid #C6D8D3',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: '#EB5E55',
            boxShadow: '0 4px 12px rgba(235, 94, 85, 0.12)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderColor: '#C6D8D3',
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: '#EB5E55',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#EB5E55',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 600,
        },
      },
    },
  },
});

// Landing Page Component
function LandingPage() {
  const navigate = useNavigate();
  const { settings } = usePlatformSettings();
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('estimatorLanguage') || 'en';
  });

  const t = translations[language];

  useEffect(() => {
    localStorage.setItem('estimatorLanguage', language);
  }, [language]);

  const handleLanguageChange = (event, newLanguage) => {
    if (newLanguage) {
      setLanguage(newLanguage);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Typography 
            variant="h6" 
            sx={{ 
              flexGrow: 1, 
              cursor: 'pointer',
              fontWeight: 700,
              color: '#3A3335',
            }}
            onClick={() => navigate('/')}
          >
            {settings.platformName}
          </Typography>
          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={handleLanguageChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: '#5C5658',
                borderColor: '#C6D8D3',
                py: 0.5,
                px: 1.5,
                '&.Mui-selected': {
                  color: '#EB5E55',
                  bgcolor: 'rgba(235, 94, 85, 0.08)',
                  borderColor: '#EB5E55',
                },
              },
            }}
          >
            <ToggleButton value="en">EN</ToggleButton>
            <ToggleButton value="es">ES</ToggleButton>
          </ToggleButtonGroup>
        </Toolbar>
      </AppBar>

      {/* Hero Section with warm gradient background */}
      <Box 
        sx={{ 
          background: 'linear-gradient(180deg, #FDF0D5 0%, #FFFFFF 100%)',
          pt: 10,
          pb: 12,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h1" 
              sx={{ 
                mb: 3, 
                fontWeight: 700,
                color: '#1F2937',
                fontSize: { xs: '2.2rem', md: '3.2rem' },
                lineHeight: 1.2,
              }}
            >
              {t.landing.heroTitle}
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 5, 
                color: '#4B5563',
                fontWeight: 400,
                maxWidth: '700px',
                margin: '0 auto 2.5rem',
                lineHeight: 1.6,
              }}
            >
              {t.landing.heroSubtitle}
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/estimate')}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.15rem',
                borderRadius: '12px',
              }}
            >
              {t.landing.ctaButton}
            </Button>

            <Typography 
              variant="body2" 
              sx={{ 
                mt: 3, 
                color: '#6B7280',
              }}
            >
              {t.landing.noObligation}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -4 }}>
        {/* Features Section */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
          }}>
            {/* Feature Card 1 */}
            <Paper sx={{ 
              p: 4, 
              bgcolor: '#FFFFFF', 
              border: '1px solid #C6D8D3',
              borderRadius: '16px',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: '#EB5E55',
                boxShadow: '0 8px 24px rgba(235, 94, 85, 0.12)',
                transform: 'translateY(-2px)',
              }
            }}>
              <Box 
                sx={{ 
                  width: 56,
                  height: 56,
                  borderRadius: '12px',
                  bgcolor: 'rgba(235, 94, 85, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <Typography sx={{ fontSize: '1.8rem' }}>📊</Typography>
              </Box>
              <Typography variant="h6" sx={{ mb: 1.5, color: '#3A3335', fontWeight: 600 }}>
                {t.landing.feature1Title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5C5658', lineHeight: 1.7 }}>
                {t.landing.feature1Text}
              </Typography>
            </Paper>

            {/* Feature Card 2 */}
            <Paper sx={{ 
              p: 4, 
              bgcolor: '#FFFFFF', 
              border: '1px solid #C6D8D3',
              borderRadius: '16px',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: '#D81E5B',
                boxShadow: '0 8px 24px rgba(216, 30, 91, 0.12)',
                transform: 'translateY(-2px)',
              }
            }}>
              <Box 
                sx={{ 
                  width: 56,
                  height: 56,
                  borderRadius: '12px',
                  bgcolor: 'rgba(216, 30, 91, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <Typography sx={{ fontSize: '1.8rem' }}>🔒</Typography>
              </Box>
              <Typography variant="h6" sx={{ mb: 1.5, color: '#3A3335', fontWeight: 600 }}>
                {t.landing.feature2Title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5C5658', lineHeight: 1.7 }}>
                {t.landing.feature2Text}
              </Typography>
            </Paper>

            {/* Feature Card 3 */}
            <Paper sx={{ 
              p: 4, 
              bgcolor: '#FFFFFF', 
              border: '1px solid #C6D8D3',
              borderRadius: '16px',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: '#EB5E55',
                boxShadow: '0 8px 24px rgba(235, 94, 85, 0.12)',
                transform: 'translateY(-2px)',
              }
            }}>
              <Box 
                sx={{ 
                  width: 56,
                  height: 56,
                  borderRadius: '12px',
                  bgcolor: 'rgba(198, 216, 211, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <Typography sx={{ fontSize: '1.8rem' }}>⚖️</Typography>
              </Box>
              <Typography variant="h6" sx={{ mb: 1.5, color: '#3A3335', fontWeight: 600 }}>
                {t.landing.feature3Title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5C5658', lineHeight: 1.7 }}>
                {t.landing.feature3Text}
              </Typography>
            </Paper>
          </Box>
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', pb: 10 }}>
          <Paper
            sx={{
              p: 6,
              bgcolor: '#3A3335',
              borderRadius: '20px',
              maxWidth: 650,
              margin: '0 auto',
            }}
          >
            <Typography variant="h4" sx={{ mb: 2, color: '#FDF0D5', fontWeight: 600 }}>
              {language === 'es' ? '¿Estuvo en un accidente?' : 'Were you in an accident?'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'rgba(253, 240, 213, 0.85)', lineHeight: 1.8 }}>
              {language === 'es' 
                ? 'Descubra lo que valen casos similares. Solo toma 2 minutos y es 100% gratis.'
                : 'Find out what similar cases are worth. It only takes 2 minutes and is 100% free.'}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/estimate')}
              sx={{
                bgcolor: '#EB5E55',
                color: '#FFFFFF',
                px: 5,
                py: 1.75,
                fontSize: '1.1rem',
                fontWeight: 700,
                borderRadius: '12px',
                boxShadow: '0 4px 14px rgba(235, 94, 85, 0.4)',
                '&:hover': {
                  bgcolor: '#D94A41',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(235, 94, 85, 0.5)',
                }
              }}
            >
              {t.landing.ctaButton}
            </Button>
          </Paper>
        </Box>

        {/* Footer */}
        <Box 
          sx={{ 
            borderTop: '1px solid #C6D8D3',
            pt: 6,
            pb: 6,
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', md: 'flex-start' },
            gap: 4
          }}>
            {/* Brand */}
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 1,
                  fontWeight: 700,
                  color: '#3A3335',
                }}
              >
                {settings.platformName}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5C5658' }}>
                {settings.platformTagline}
              </Typography>
            </Box>

            {/* Links */}
            <Box sx={{ 
              display: 'flex', 
              gap: 4,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center'
            }}>
              <Typography
                sx={{ 
                  color: '#5C5658',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  '&:hover': { 
                    color: '#EB5E55',
                  },
                  transition: 'color 0.2s'
                }}
              >
                {language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
              </Typography>
              <Typography
                sx={{ 
                  color: '#5C5658',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  '&:hover': { 
                    color: '#EB5E55',
                  },
                  transition: 'color 0.2s'
                }}
              >
                {language === 'es' ? 'Términos de Servicio' : 'Terms of Service'}
              </Typography>
            </Box>
          </Box>

          {/* Disclaimer */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#7A7577',
              textAlign: 'center',
              mt: 4,
              fontSize: '0.75rem',
              lineHeight: 1.6,
              maxWidth: 800,
              margin: '2rem auto 0',
            }}
          >
            {language === 'es' 
              ? 'Este estimador proporciona rangos basados en datos históricos de acuerdos y no constituye asesoramiento legal. Los resultados reales varían según las circunstancias individuales del caso.'
              : 'This estimator provides ranges based on historical settlement data and does not constitute legal advice. Actual results vary based on individual case circumstances.'}
          </Typography>

          {/* Copyright */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#7A7577',
              textAlign: 'center',
              mt: 3,
              fontSize: '0.85rem'
            }}
          >
            © {new Date().getFullYear()} {settings.companyName}. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

// Main App with routing
function App() {
  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/estimate" element={<EstimatorWizard />} />
          
          {/* Auth Routes */}
          <Route path="/admin" element={<LoginPage />} />
          
          {/* Protected Admin Routes */}
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/leads" element={<UserLeadsPage />} />
        </Routes>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
